import { Api, Endpoint } from '@square/ignition';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ReferenceHrefBuilders } from '@squareup/dex-tech-utils-routing';
import { Box } from '@squareup/dex-ui-shared-base';
import {
  MarketInputSearch,
  MarketList,
  MarketRow,
  MarketSelect,
} from '@squareup/dex-ui-shared-market';
import {
  filterForValidApiExplorerEndpoints,
  toApiId,
  toEndpointId,
} from '@squareup/dex-utils-oas';
import { humanizeName } from '@squareup/dex-utils-text';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import React, { memo, useCallback, useMemo, useRef } from 'react';

import styles from './api-explorer-endpoint-picker.module.css';

const strings = {
  selectLabel: 'Endpoint',
  selectPlaceholder: 'Select endpoint',
  searchPlaceholder: 'Search endpoints',
};

interface Props {
  selectedApi: Api | undefined;
  selectedEndpoint: Endpoint | undefined;
  currentVersion: string;
}

const ApiExplorerEndpointPicker: React.FC<Props> = memo(
  ({ selectedApi, selectedEndpoint, currentVersion }) => {
    const router = useRouter();

    const endpoints = useMemo(() => {
      return filterForValidApiExplorerEndpoints(selectedApi?.endpoints || []);
    }, [selectedApi?.endpoints]);

    const inputSearchRef =
      useRef<globalThis.HTMLMarketInputSearchElement>(null);

    const onOpen = useCallback(() => {
      inputSearchRef.current?.setFocus();
    }, []);

    const onClose = useCallback(() => {
      inputSearchRef.current?.clearInput();
    }, []);

    return (
      <Box
        id="endpoint-picker-select-container"
        className={styles['endpoint-picker-container']}
      >
        <MarketSelect
          data-testid="api-explorer-endpoint-picker-select"
          placeholder={strings.selectPlaceholder}
          size="small"
          value={selectedEndpoint?.id ?? ''}
          // Ensures the MarketSelect renders in with the correct value when the selectdApi changes.
          // Otherwise, it will flash with the placeholder value on the first render
          key={selectedApi?.id}
          onMarketSelectOpened={onOpen}
          onMarketSelectClosed={onClose}
          disabled={endpoints.length === 0}
          popoverContainer="#endpoint-picker-select-container"
        >
          <label>{strings.selectLabel}</label>
          <MarketList slot="list" className={clsx(styles.list)}>
            {endpoints.length > 1 && (
              <MarketInputSearch
                slot="search"
                size="small"
                ref={inputSearchRef}
                placeholder={strings.searchPlaceholder}
                className={clsx(styles['search-input'])}
              />
            )}
            {endpoints?.map((endpoint) => {
              const href = ReferenceHrefBuilders.versionSquareExplorerEndpoint({
                api: toApiId(selectedApi?.id),
                endpoint: toEndpointId(endpoint.id),
                square_version: currentVersion,
                ...('env' in router.query && {
                  env: router.query['env'] as 'sandbox' | 'production',
                }),
                ...('prefill' in router.query && {
                  prefill: router.query['prefill'] as string,
                }),
                ...('appId' in router.query && {
                  appId: router.query['appId'] as string,
                }),
              });

              return (
                <MarketRow
                  onMarketRowSelected={() => {
                    router.push(href);
                  }}
                  size="small"
                  key={endpoint.id}
                  value={endpoint.id}
                  selected={selectedEndpoint?.id === endpoint.id}
                >
                  <Box as="label" slot="label">
                    {humanizeName(endpoint.name, { style: 'sentence' })}
                  </Box>
                </MarketRow>
              );
            })}
          </MarketList>
        </MarketSelect>
      </Box>
    );
  }
);

ApiExplorerEndpointPicker.displayName = 'ApiExplorerEndpointPicker';

export { ApiExplorerEndpointPicker };
